import { spacing, typography } from "../../app/design-system";
import { Progress,Spin,Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useAuth } from "../auth/useAuth";
import { useI18n } from "../i18n/useI18n";
import { formatPercent } from "../units/format";
import { useEffectiveUnits } from "../units/useEffectiveUnits";
import { type JobInfo,useJobTracker } from "../jobs/JobTrackerContext";
import { FullScreenWaitBackdrop } from "../../../../../../shared/frontend/components/FullScreenWaitBackdrop";

const PERCENT_MAX = 100;

const TENANT_BACKUP_RESTORE_JOB_TYPE = "TenantBackupRestore";

function isActiveRestoreJob(job: JobInfo): boolean {
  return job.jobType === TENANT_BACKUP_RESTORE_JOB_TYPE
    && !job.finishedAt
    && !job.error
    && (job.status === "Queued" || job.status === "Running");
}

function getRestorePercent(job: JobInfo): number {
  if (job.total <= 0) return 0;
  return Math.max(0, Math.min(PERCENT_MAX, Math.round((job.current / job.total) * PERCENT_MAX)));
}

export function TenantBackupRestoreOverlay() {
  const units = useEffectiveUnits();
  const { jobs } = useJobTracker();
  const { info } = useAuth();
  const { text } = useI18n();
  const currentTenantId = (info?.tenantId ?? "").trim();

  const restoreJob = React.useMemo(() => {
    if (!currentTenantId) return null;
    return Object.values(jobs)
      .filter((job) => isActiveRestoreJob(job) && (job.tenantId ?? "").trim() === currentTenantId)
      .sort((a, b) => (b.startedAt ?? b.id).localeCompare(a.startedAt ?? a.id))[0] ?? null;
  }, [jobs, currentTenantId]);

  if (!restoreJob) return null;

  const percent = getRestorePercent(restoreJob);
  const hasServerProgress = restoreJob.current > 0 && restoreJob.total > 0;

  return (
    <FullScreenWaitBackdrop zIndex={10060}>
      <div
        role="dialog"
        aria-modal
        aria-labelledby="tenant-backup-restore-overlay-title"
        style={{ display: "grid", gap: spacing.relaxed, textAlign: "center" }}
      >
        <Typography.Title
          id="tenant-backup-restore-overlay-title"
          level={3}
          style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: 600 }}
        >
          {text("Tenant backup restore blocking title")}
        </Typography.Title>
        <Spin size="large" />
        <Progress
          percent={percent}
          status="active"
          format={() => hasServerProgress ? formatPercent(percent, units) : text("Tenant backup restore progress pending")}
          style={{ width: "min(420px, 100%)", margin: "0 auto" }}
        />
        <Typography.Text type="secondary">
          {restoreJob.message ? text(restoreJob.message) : text("Tenant backup restore blocking hint")}
        </Typography.Text>
      </div>
    </FullScreenWaitBackdrop>
  );
}
